// Free app-to-app calls between colleagues: browser WebRTC, voice goes device to
// device (never through SignalWire or the phone network). The backend socket at
// /api/telesales/rtc only rings, accepts and passes connection details.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const AppCallContext = createContext(null);
export const useAppCalls = () => useContext(AppCallContext) || { online: new Set(), onCall: new Set(), ready: false, call: () => {}, state: null, subscribe: () => () => {} };

const socketUrl = () => `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, 'ws')}/api/telesales/rtc`;

const ENDED = {
  declined: 'Call declined', no_answer: 'No answer', canceled: 'Call canceled', completed: 'Call ended',
  disconnected: 'Call dropped', offline: 'They are offline right now', busy: 'They are on another call',
};

export function AppCallProvider({ children }) {
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const pendingIce = useRef([]);
  const iceServers = useRef([]);
  const callRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(new Set());
  const [onCall, setOnCall] = useState(new Set());
  const listeners = useRef(new Set());
  // Pages (Owner Control) listen to the same socket for live updates.
  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const [call, setCallState] = useState(null); // {id, peer:{id,name}, role:'caller'|'callee', status, since}
  const [muted, setMuted] = useState(false);
  const [now, setNow] = useState(Date.now());

  const setCall = (c) => { callRef.current = c; setCallState(c); };
  const send = (msg) => { const ws = wsRef.current; if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg)); };

  const cleanup = useCallback(() => {
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) { /* already closed */ } }
    pcRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    pendingIce.current = [];
    window.__tsAppCall = null;
    setMuted(false);
    setCall(null);
  }, []);

  const peer = useCallback(async (callId) => {
    const pc = new RTCPeerConnection({ iceServers: iceServers.current });
    pcRef.current = pc;
    window.__tsAppCall = pc; // read by the browser end-to-end test
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: 'signal', call_id: callId, data: { kind: 'ice', candidate: e.candidate.toJSON() } }); };
    pc.ontrack = (e) => { if (audioRef.current) { audioRef.current.srcObject = e.streams[0]; audioRef.current.play().catch(() => {}); } };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected' && callRef.current) setCall({ ...callRef.current, status: 'active', since: Date.now() });
      if (pc.connectionState === 'failed') { toast.error('The call could not connect.'); send({ type: 'hangup', call_id: callId }); cleanup(); }
    };
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    streamRef.current = stream;
    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    return pc;
  }, [cleanup]);

  const flushIce = async () => {
    const pc = pcRef.current;
    while (pc && pendingIce.current.length) { try { await pc.addIceCandidate(pendingIce.current.shift()); } catch (e) { /* stale */ } }
  };

  const onMessage = useCallback(async (msg) => {
    const cur = callRef.current;
    listeners.current.forEach(fn => { try { fn(msg); } catch (e) { /* a page listener failed */ } });
    switch (msg.type) {
      case 'revoked':
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        break;
      case 'ready': iceServers.current = msg.ice_servers || []; setReady(true); break;
      case 'presence': setOnline(new Set(msg.online)); setOnCall(new Set(msg.on_call || [])); break;
      case 'ringing': if (cur && cur.role === 'caller') setCall({ ...cur, id: msg.call_id, status: 'ringing' }); break;
      case 'unavailable': toast.message(ENDED[msg.reason] || 'Not available'); cleanup(); break;
      case 'incoming':
        if (cur) { send({ type: 'decline', call_id: msg.call_id }); break; }
        setCall({ id: msg.call_id, peer: msg.from, role: 'callee', status: 'incoming' });
        break;
      case 'accepted':
        if (!cur || cur.id !== msg.call_id) break;
        setCall({ ...cur, status: 'connecting' });
        try {
          const pc = await peer(msg.call_id);
          await pc.setLocalDescription(await pc.createOffer());
          send({ type: 'signal', call_id: msg.call_id, data: { kind: 'offer', sdp: pc.localDescription.sdp } });
        } catch (e) { toast.error('Microphone is not available.'); send({ type: 'hangup', call_id: msg.call_id }); cleanup(); }
        break;
      case 'signal': {
        if (!cur || cur.id !== msg.call_id) break;
        const d = msg.data || {};
        try {
          if (d.kind === 'offer') {
            const pc = pcRef.current || await peer(msg.call_id);
            await pc.setRemoteDescription({ type: 'offer', sdp: d.sdp });
            await flushIce();
            await pc.setLocalDescription(await pc.createAnswer());
            send({ type: 'signal', call_id: msg.call_id, data: { kind: 'answer', sdp: pc.localDescription.sdp } });
          } else if (d.kind === 'answer' && pcRef.current) {
            await pcRef.current.setRemoteDescription({ type: 'answer', sdp: d.sdp });
            await flushIce();
          } else if (d.kind === 'ice') {
            if (pcRef.current && pcRef.current.remoteDescription) await pcRef.current.addIceCandidate(d.candidate);
            else pendingIce.current.push(d.candidate);
          }
        } catch (e) { toast.error('Microphone is not available.'); send({ type: 'hangup', call_id: msg.call_id }); cleanup(); }
        break;
      }
      case 'answered_elsewhere': if (cur && cur.id === msg.call_id) cleanup(); break;
      case 'ended': if (cur && cur.id === msg.call_id) { toast.message(ENDED[msg.reason] || 'Call ended'); cleanup(); } break;
      case 'error': toast.error(msg.message); if (cur && !cur.id) cleanup(); break;
      default:
    }
  }, [cleanup, peer]);

  useEffect(() => {
    let closed = false;
    let retry = 1000;
    let timer = null;
    const open = () => {
      const token = localStorage.getItem('auth_token');
      if (!token || closed) return;
      const ws = new WebSocket(socketUrl());
      wsRef.current = ws;
      ws.onopen = () => { retry = 1000; ws.send(JSON.stringify({ type: 'auth', token })); };
      ws.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch (err) { /* ignore bad frame */ } };
      ws.onclose = (e) => {
        setReady(false);
        setOnline(new Set());
        if (callRef.current) cleanup();
        if (!closed && e.code !== 4401) { timer = setTimeout(open, retry); retry = Math.min(retry * 2, 30000); }
      };
    };
    open();
    const ping = setInterval(() => send({ type: 'ping' }), 25000);
    return () => { closed = true; clearTimeout(timer); clearInterval(ping); if (wsRef.current) wsRef.current.close(); cleanup(); };
  }, [onMessage, cleanup]);

  useEffect(() => {
    if (call?.status !== 'active') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [call?.status]);

  const start = (member) => {
    if (callRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) { toast.error('This browser cannot make app calls.'); return; }
    setCall({ id: null, peer: { id: member.id, name: member.full_name }, role: 'caller', status: 'calling' });
    send({ type: 'call', to: member.id });
  };
  const accept = () => { if (call) { setCall({ ...call, status: 'connecting' }); send({ type: 'accept', call_id: call.id }); } };
  const decline = () => { if (call) { send({ type: 'decline', call_id: call.id }); cleanup(); } };
  const hangup = () => { if (call) { if (call.id) send({ type: 'hangup', call_id: call.id }); cleanup(); } };
  const toggleMute = () => {
    const next = !muted;
    (streamRef.current?.getAudioTracks() || []).forEach(t => { t.enabled = !next; });
    setMuted(next);
  };

  const secs = call?.since ? Math.max(0, Math.floor((now - call.since) / 1000)) : 0;
  const label = {
    calling: 'Calling…', ringing: 'Ringing…', incoming: 'is calling you (free app call)', connecting: 'Connecting…',
    active: `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`,
  }[call?.status] || '';

  return (
    <AppCallContext.Provider value={{ ready, online, onCall, call, start, subscribe }}>
      {children}
      <audio ref={audioRef} autoPlay data-testid="ts-app-call-audio" />
      {call && (
        <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-lg p-4 space-y-3" data-testid="ts-app-call">
          <div>
            <div className="text-sm font-semibold text-foreground">{call.peer?.name}</div>
            <div className="text-xs text-muted-foreground" data-testid="ts-app-call-status" data-status={call.status}>{label}</div>
          </div>
          <div className="flex gap-2">
            {call.status === 'incoming' ? (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" onClick={accept} data-testid="ts-app-call-accept"><Phone className="w-4 h-4 mr-1" /> Answer</Button>
                <Button size="sm" variant="outline" className="text-red-500 flex-1" onClick={decline} data-testid="ts-app-call-decline">Decline</Button>
              </>
            ) : (
              <>
                {call.status === 'active' && (
                  <Button size="sm" variant="outline" onClick={toggleMute} data-testid="ts-app-call-mute">
                    {muted ? <MicOff className="w-4 h-4 mr-1" /> : <Mic className="w-4 h-4 mr-1" />} {muted ? 'Unmute' : 'Mute'}
                  </Button>
                )}
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white flex-1" onClick={hangup} data-testid="ts-app-call-hangup"><PhoneOff className="w-4 h-4 mr-1" /> Hang up</Button>
              </>
            )}
          </div>
        </div>
      )}
    </AppCallContext.Provider>
  );
}
